import { useState } from 'react';
import { Plus, Pencil, Trash2, Loader2, Check, X, Tag, Lock, ShieldCheck } from 'lucide-react';
import { useContactTags } from '@/features/crm/hooks/useContacts';
import {
  useCreateTag,
  useCreatePlatformTag,
  useUpdateTag,
  useDeleteTag,
} from '@/features/crm/hooks/useCategoryMutations';
import { useAuth } from '@/app/providers/AuthProvider';
import { ColorPicker } from './ColorPicker';
import { COLOR_PALETTE, type ContactTagRow } from '@/features/crm/types';

/* ─── Linha editável ─────────────────────────────────────────────────────────── */
function TagRow({ tag, isSuperAdmin }: { tag: ContactTagRow; isSuperAdmin: boolean }) {
  const [editing, setEditing] = useState(false);
  const [label,   setLabel]   = useState(tag.label);
  const [color,   setColor]   = useState(tag.color_class);

  const updateMutation = useUpdateTag();
  const deleteMutation = useDeleteTag();

  const isPlatformDefault = tag.tenant_id === null;
  const canEdit = !isPlatformDefault || isSuperAdmin;

  const handleSave = () => {
    if (!label.trim()) return;
    updateMutation.mutate(
      { id: tag.id, label: label.trim(), color_class: color },
      { onSuccess: () => setEditing(false) },
    );
  };

  const handleDelete = () => {
    const msg = isPlatformDefault
      ? `Remover a sugestão de plataforma "${tag.label}"? Todos os tenants perderão essa tag.`
      : `Remover a tag "${tag.label}"? Ela será desvinculada dos contatos.`;
    if (!window.confirm(msg)) return;
    deleteMutation.mutate(tag.id);
  };

  const handleCancel = () => {
    setLabel(tag.label);
    setColor(tag.color_class);
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="card-premium rounded-xl p-4 space-y-3 border-accent/20">
        <div className="space-y-1">
          <label className="font-mono text-[10px] uppercase tracking-[0.18em] font-semibold text-ink-muted">
            Nome da tag
          </label>
          <input
            autoFocus
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter')  handleSave();
              if (e.key === 'Escape') handleCancel();
            }}
            className="w-full rounded-xl bg-midnight-2/60 border border-accent/40 px-3 py-2 text-sm text-ink placeholder-ink-faint focus:outline-none focus:border-accent/70 transition-all"
          />
        </div>

        <div className="space-y-2">
          <label className="font-mono text-[10px] uppercase tracking-[0.18em] font-semibold text-ink-muted">Cor</label>
          <ColorPicker value={color} onChange={setColor} />
        </div>

        <div className="flex items-center gap-2">
          <span className="font-mono text-[10px] uppercase tracking-wider text-ink-subtle">Preview:</span>
          <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${color}`}>
            {label || 'Tag'}
          </span>
        </div>

        <div className="flex gap-2 pt-1">
          <button
            type="button"
            onClick={handleSave}
            disabled={updateMutation.isPending || !label.trim()}
            className="flex items-center gap-1.5 rounded-lg btn-accent px-3 py-1.5 text-xs font-semibold cursor-pointer disabled:opacity-50"
          >
            {updateMutation.isPending
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : <Check className="h-3.5 w-3.5" />
            }
            Salvar
          </button>
          <button
            type="button"
            onClick={handleCancel}
            className="rounded-lg btn-ghost px-3 py-1.5 text-xs font-semibold cursor-pointer"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-3 px-4 py-3 rounded-xl border border-hairline transition-colors ${
      isPlatformDefault && !isSuperAdmin
        ? 'bg-midnight-2/20 opacity-70'
        : 'bg-midnight-2/40 hover:bg-midnight-2/60'
    }`}>
      <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-semibold shrink-0 ${tag.color_class}`}>
        {tag.label}
      </span>

      <div className="flex-grow min-w-0">
        <p className="text-sm font-semibold text-ink truncate">{tag.label}</p>
        <p className="text-[10px] text-ink-subtle font-mono mt-0.5">{tag.slug}</p>
        {isPlatformDefault && !isSuperAdmin && (
          <p className="text-[10px] text-ink-subtle flex items-center gap-1 mt-0.5">
            <Lock className="h-3 w-3" />
            Sugestão de plataforma — somente leitura
          </p>
        )}
        {isPlatformDefault && isSuperAdmin && (
          <p className="text-[10px] text-accent flex items-center gap-1 mt-0.5">
            <ShieldCheck className="h-3 w-3" />
            Sugestão de plataforma — visível a todos os tenants
          </p>
        )}
      </div>

      {canEdit && (
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={() => setEditing(true)}
            className="rounded-lg p-1.5 text-ink-muted hover:bg-white/5 hover:text-ink transition-colors cursor-pointer"
            title="Editar"
          >
            <Pencil className="h-4 w-4" />
          </button>
          <button
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
            className="rounded-lg p-1.5 text-ink-muted hover:bg-danger/10 hover:text-danger transition-colors cursor-pointer disabled:opacity-40"
            title="Remover"
          >
            {deleteMutation.isPending
              ? <Loader2 className="h-4 w-4 animate-spin" />
              : <Trash2 className="h-4 w-4" />
            }
          </button>
        </div>
      )}
    </div>
  );
}

/* ─── Formulário genérico de nova tag ────────────────────────────────────────── */
function NewTagForm({
  existingCount,
  platformDefault = false,
}: {
  existingCount: number;
  platformDefault?: boolean;
}) {
  const [open,  setOpen]  = useState(false);
  const [label, setLabel] = useState('');
  const [color, setColor] = useState(COLOR_PALETTE[existingCount % COLOR_PALETTE.length]);

  const createTenant   = useCreateTag();
  const createPlatform = useCreatePlatformTag();
  const mutation = platformDefault ? createPlatform : createTenant;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!label.trim()) return;
    mutation.mutate(
      { label: label.trim(), colorIndex: existingCount },
      {
        onSuccess: () => {
          setLabel('');
          setColor(COLOR_PALETTE[(existingCount + 1) % COLOR_PALETTE.length]);
          setOpen(false);
        },
      },
    );
  };

  const btnLabel    = platformDefault ? 'Nova sugestão de plataforma' : 'Nova tag personalizada';
  const createLabel = platformDefault ? 'Criar sugestão' : 'Criar tag';

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 w-full rounded-xl border border-dashed border-hairline px-4 py-3 text-sm text-ink-muted hover:border-accent/40 hover:text-accent transition-all cursor-pointer"
      >
        <Plus className="h-4 w-4" />
        {btnLabel}
      </button>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="card-premium rounded-xl p-4 space-y-3 border-accent/20">
      <p className="text-sm font-semibold text-ink">{platformDefault ? 'Nova sugestão de plataforma' : 'Nova tag'}</p>

      <div className="space-y-1">
        <label className="font-mono text-[10px] uppercase tracking-[0.18em] font-semibold text-ink-muted">Nome</label>
        <input
          autoFocus
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder={platformDefault ? 'Ex.: Zona Norte, Deficiente Visual…' : 'Ex.: Evangélico, Zona Norte, Pastor…'}
          className="w-full rounded-xl bg-midnight-2/60 border border-hairline px-3 py-2 text-sm text-ink placeholder-ink-faint focus:outline-none focus:border-accent/50 transition-all"
        />
      </div>

      <div className="space-y-2">
        <label className="font-mono text-[10px] uppercase tracking-[0.18em] font-semibold text-ink-muted">Cor</label>
        <ColorPicker value={color} onChange={setColor} />
      </div>

      <div className="flex items-center gap-2">
        <span className="font-mono text-[10px] uppercase tracking-wider text-ink-subtle">Preview:</span>
        <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${color}`}>
          {label || 'Tag'}
        </span>
      </div>

      <div className="flex gap-2 pt-1">
        <button
          type="submit"
          disabled={mutation.isPending || !label.trim()}
          className="flex items-center gap-1.5 rounded-lg btn-accent px-3 py-1.5 text-xs font-semibold cursor-pointer disabled:opacity-50"
        >
          {mutation.isPending
            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
            : <Plus className="h-3.5 w-3.5" />
          }
          {createLabel}
        </button>
        <button
          type="button"
          onClick={() => { setOpen(false); setLabel(''); }}
          className="rounded-lg btn-ghost px-3 py-1.5 text-xs font-semibold cursor-pointer"
        >
          Cancelar
        </button>
      </div>
    </form>
  );
}

/* ─── Página principal ────────────────────────────────────────────────────────── */
export function TagsSettings() {
  const { isSuperAdmin } = useAuth();
  const { data: tags = [], isLoading } = useContactTags();

  const defaults = tags.filter((t) => t.tenant_id === null);
  const custom   = tags.filter((t) => t.tenant_id !== null);

  return (
    <div className="space-y-8 max-w-xl">
      {/* Intro */}
      <div className="card-premium rounded-2xl px-5 py-4">
        <p className="text-sm text-ink-muted leading-relaxed">
          Tags são marcadores livres que enriquecem o perfil de cada contato.
          Um contato pode ter <strong className="text-ink">múltiplas tags</strong> ao mesmo tempo.
          As <strong className="text-ink">sugestões de plataforma</strong> estão disponíveis para todos os tenants
          {isSuperAdmin
            ? ' e podem ser gerenciadas por superadmins.'
            : '. Crie tags personalizadas para o contexto da sua campanha.'}
        </p>
      </div>

      {isLoading ? (
        <div className="flex items-center gap-3 text-ink-muted py-8 justify-center">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span className="text-sm">Carregando tags…</span>
        </div>
      ) : (
        <>
          {/* Sugestões de plataforma */}
          {(defaults.length > 0 || isSuperAdmin) && (
            <section className="space-y-3">
              <h2 className="font-mono text-[10px] uppercase tracking-[0.2em] font-semibold text-ink-subtle">
                <span className="flex-grow hairline-b pb-1 flex items-center gap-2">
                  Sugestões de plataforma
                  {isSuperAdmin && (
                    <span className="ml-2 font-mono text-[9px] bg-accent/15 text-accent border border-accent/25 rounded-full px-2 py-0.5 normal-case tracking-normal inline-flex items-center gap-1">
                      <ShieldCheck className="h-2.5 w-2.5" /> editável
                    </span>
                  )}
                </span>
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {defaults.map((tag) => (
                  <TagRow key={tag.id} tag={tag} isSuperAdmin={isSuperAdmin} />
                ))}
              </div>
              {isSuperAdmin && (
                <NewTagForm existingCount={defaults.length} platformDefault />
              )}
            </section>
          )}

          {/* Tags personalizadas do tenant */}
          <section className="space-y-3">
            <h2 className="font-mono text-[10px] uppercase tracking-[0.2em] font-semibold text-ink-subtle">
              <span className="flex-grow hairline-b pb-1 block">
                Personalizadas
                {custom.length > 0 && (
                  <span className="ml-2 font-mono text-[9px] bg-accent/15 text-accent border border-accent/25 rounded-full px-2 py-0.5 normal-case tracking-normal">
                    {custom.length}
                  </span>
                )}
              </span>
            </h2>

            {custom.length === 0 ? (
              <div className="flex flex-col items-center gap-3 py-8 text-ink-muted">
                <Tag className="h-8 w-8 text-ink-subtle opacity-50" />
                <p className="text-sm">Nenhuma tag personalizada ainda.</p>
                <p className="text-xs text-ink-subtle text-center max-w-xs">
                  Crie tags específicas para o seu contexto — bairros, perfis,
                  grupos de interesse. Você também pode adicioná-las diretamente
                  no formulário de contato.
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {custom.map((tag) => (
                  <TagRow key={tag.id} tag={tag} isSuperAdmin={isSuperAdmin} />
                ))}
              </div>
            )}

            <NewTagForm existingCount={custom.length} />
          </section>
        </>
      )}
    </div>
  );
}
